import React from "react";
import EmptyImage from "../EmptyImage.tsx";
import { Incident, Unschedule } from "../../models/Incidents.ts";
import RowIncident from "./RowIncident.tsx";
import UnscheduleRow from "./UnscheduleRow.tsx";
import IncidentViewTabs, {
  type IncidentViewMode,
} from "./IncidentViewTabs.tsx";
import type { IncidentAfterSavePayload } from "./incidentTableTypes.ts";
import type { BranchReportMeta } from "../../utils/payrollBranchMeta.ts";

export type IncidentsTableProps = {
  records: Incident[];
  unschedule: Unschedule[];
  removeRecord: (scheduleId: number) => void;
  removeUnscheduleRecord: (partnerId: number, checkInId: number) => void;
  onAfterSave?: (payload: IncidentAfterSavePayload) => void;
  loading?: boolean;
  updateIncidentRecord?: (scheduleId: number, updates: { type?: string; reason?: string }) => void;
  incidentViewMode?: IncidentViewMode;
  onViewModeChange?: (mode: IncidentViewMode) => void;
  isAdmin?: boolean;
  hidePlannedColumns?: boolean;
  branchMetaMap?: Record<number, BranchReportMeta>;
  maxNightCutoffMinutes?: number;
  /** Día operativo de la consulta (incidencias por sucursal / jefe). */
  operationDate?: string;
  /** Jefe directo: muestra la sucursal donde ocurrió la incidencia. */
  showBranchName?: boolean;
  branchNameById?: Record<number, string>;
};

const Incidents = ({
  records,
  unschedule,
  removeRecord,
  removeUnscheduleRecord,
  onAfterSave,
  loading = false,
  updateIncidentRecord,
  incidentViewMode = 'pending',
  onViewModeChange,
  isAdmin = false,
  hidePlannedColumns = false,
  branchMetaMap = {},
  maxNightCutoffMinutes,
  operationDate,
  showBranchName = false,
  branchNameById = {},
}: IncidentsTableProps) => {
  const totalIncidencias = records.length;
  const totalNoProgramados = unschedule.length;
  const totalGeneral = totalIncidencias + totalNoProgramados;

  return (
    <div>
      <style>{`
        .incidents-summary-card {
          width: 100%;
          min-width: 0;
          box-sizing: border-box;
          background: linear-gradient(135deg, #495057 0%, #343a40 100%);
          border-radius: 12px;
          margin-bottom: 16px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          padding: 16px 20px;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 12px;
        }
        .incidents-summary-stat {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #ffffff;
          text-align: left;
        }
        .incidents-summary-stat-icon {
          font-size: 16px;
          line-height: 1;
          opacity: 0.85;
          flex-shrink: 0;
        }
        .incidents-summary-stat-icon--accent {
          color: #ffc107;
          opacity: 1;
          font-size: 20px;
        }
        .incidents-summary-stat-label {
          font-size: 14px;
          font-weight: 600;
          opacity: 0.95;
          line-height: 1.2;
        }
        .incidents-summary-stat-value {
          font-size: 22px;
          font-weight: 700;
          line-height: 1;
        }
        .incidents-summary-stat-value--accent {
          color: #ffc107;
          font-size: 24px;
        }
        .incidents-summary-stat--secondary .incidents-summary-stat-label {
          font-size: 13px;
          font-weight: 500;
          opacity: 0.9;
        }
        .incidents-summary-stat--secondary .incidents-summary-stat-value {
          font-size: 18px;
          font-weight: 600;
        }
        .incidents-summary-secondary {
          display: flex;
          align-items: center;
          gap: 24px;
          flex-wrap: wrap;
        }
        @media (min-width: 768px) {
          .incidents-summary-card {
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            padding: 16px 24px;
            gap: 24px;
          }
          .incidents-summary-stat-label {
            font-size: 16px;
          }
          .incidents-summary-stat-value--accent {
            font-size: 24px;
          }
        }
      `}</style>

      <div className="incidents-summary-card">
        <div className="incidents-summary-stat incidents-summary-stat--total">
          <i
            className="fa fa-exclamation-triangle incidents-summary-stat-icon incidents-summary-stat-icon--accent"
            aria-hidden
          />
          <span className="incidents-summary-stat-label">Total de incidencias:</span>
          <span className="incidents-summary-stat-value incidents-summary-stat-value--accent">
            {totalGeneral}
          </span>
        </div>

        <div className="incidents-summary-secondary">
          <div className="incidents-summary-stat incidents-summary-stat--secondary">
            <i className="fa fa-calendar-check incidents-summary-stat-icon" aria-hidden />
            <span className="incidents-summary-stat-label">Programadas:</span>
            <span className="incidents-summary-stat-value">{totalIncidencias}</span>
          </div>

          <div className="incidents-summary-stat incidents-summary-stat--secondary">
            <i className="fa fa-clock-rotate-left incidents-summary-stat-icon" aria-hidden />
            <span className="incidents-summary-stat-label">No programados:</span>
            <span className="incidents-summary-stat-value">{totalNoProgramados}</span>
          </div>
        </div>
      </div>

      {onViewModeChange && (
        <IncidentViewTabs
          value={incidentViewMode}
          onChange={onViewModeChange}
        />
      )}

      <div
        id="incidents-table-panel"
        role="tabpanel"
        aria-labelledby={`incidents-tab-${incidentViewMode}`}
        className="table-sticky mb-3 incidents-table-panel"
        key={incidentViewMode}
        style={{
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          maxHeight: "calc(100vh - 280px)",
          overflowY: "auto",
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          animation: "incidentsPanelFadeIn 0.32s ease-out",
        }}
      >
        <style>{`
          @keyframes incidentsPanelFadeIn {
            from { opacity: 0; transform: translateY(6px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
        <table
          className="table table-hover align-middle mb-0 incidents-data-table"
          style={{
            fontSize: "12px",
            minWidth: "760px",
            width: "100%",
            backgroundColor: "white",
            borderCollapse: "separate",
            borderSpacing: 0,
          }}
        >
          <thead
            className="sticky-thead text-center"
            style={{
              background: "#495057",
              borderTop: "1px solid #dee2e6",
              borderBottom: "1px solid #dee2e6",
              borderLeft: "none",
              borderRight: "none",
              color: "white",
              boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
            }}
          >
            <tr>
              <th
                rowSpan={2}
                className="align-middle"
                style={{
                  padding: "6px 8px",
                  fontWeight: "600",
                  fontSize: "11px",
                  color: "white",
                  borderLeft: "none",
                  borderRight: "none",
                  borderTop: "none",
                  borderBottom: "none",
                }}
              >
                Colaborador
              </th>
              <th
                colSpan={2}
                className="align-middle"
                style={{
                  padding: "6px 8px",
                  fontWeight: "600",
                  fontSize: "11px",
                  color: "white",
                  borderLeft: "1px solid rgba(255, 255, 255, 0.3)",
                  borderRight: "1px solid rgba(255, 255, 255, 0.3)",
                  borderTop: "none",
                  borderBottom: "none",
                }}
              >
                Horario Planeado
              </th>
              <th
                colSpan={2}
                className="align-middle"
                style={{
                  padding: "6px 8px",
                  fontWeight: "600",
                  fontSize: "11px",
                  color: "white",
                  borderLeft: "1px solid rgba(255, 255, 255, 0.3)",
                  borderRight: "1px solid rgba(255, 255, 255, 0.3)",
                  borderTop: "none",
                  borderBottom: "none",
                }}
              >
                Incidencias
              </th>
              <th
                rowSpan={2}
                className="align-middle"
                style={{
                  padding: "6px 8px",
                  fontWeight: "600",
                  fontSize: "11px",
                  color: "white",
                  borderLeft: "1px solid rgba(255, 255, 255, 0.3)",
                  borderRight: "none",
                  borderTop: "none",
                  borderBottom: "none",
                }}
              >
                Tipo
              </th>
              <th
                rowSpan={2}
                className="align-middle"
                style={{
                  padding: "6px 8px",
                  fontWeight: "600",
                  fontSize: "11px",
                  color: "white",
                  borderLeft: "none",
                  borderRight: "none",
                  borderTop: "none",
                  borderBottom: "none",
                }}
              >
                Acción
              </th>
            </tr>
            <tr>
              <th
                style={{
                  padding: "4px 6px",
                  fontSize: "10px",
                  fontWeight: "500",
                  color: "white",
                  borderLeft: "1px solid rgba(255, 255, 255, 0.3)",
                  borderRight: "1px solid rgba(255, 255, 255, 0.3)",
                  borderTop: "none",
                  borderBottom: "none",
                }}
              >
                Entrada
              </th>
              <th
                style={{
                  padding: "4px 6px",
                  fontSize: "10px",
                  fontWeight: "500",
                  color: "white",
                  borderLeft: "none",
                  borderRight: "1px solid rgba(255, 255, 255, 0.3)",
                  borderTop: "none",
                  borderBottom: "none",
                }}
              >
                Salida
              </th>
              <th
                style={{
                  padding: "4px 6px",
                  fontSize: "10px",
                  fontWeight: "500",
                  color: "white",
                  borderLeft: "1px solid rgba(255, 255, 255, 0.3)",
                  borderRight: "1px solid rgba(255, 255, 255, 0.3)",
                  borderTop: "none",
                  borderBottom: "none",
                }}
              >
                Entrada
              </th>
              <th
                style={{
                  padding: "4px 6px",
                  fontSize: "10px",
                  fontWeight: "500",
                  color: "white",
                  borderLeft: "none",
                  borderRight: "1px solid rgba(255, 255, 255, 0.3)",
                  borderTop: "none",
                  borderBottom: "none",
                }}
              >
                Salida
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <tr
                  key={`skeleton-${index}`}
                  className="text-center"
                  style={{
                    backgroundColor: index % 2 === 0 ? "white" : "#f8f9fa",
                    borderBottom: "2px solid #dee2e6",
                    minHeight: "120px",
                  }}
                >
                  <td style={{ padding: "12px 8px", borderLeft: "none", borderRight: "none", verticalAlign: "middle" }}>
                    <div className="d-flex flex-column align-items-center">
                      <p className="card-text placeholder-glow mb-2">
                        <span className="placeholder" style={{ width: "120px", height: "16px", display: "block" }}></span>
                      </p>
                      <p className="card-text placeholder-glow mb-0">
                        <span className="placeholder" style={{ width: "150px", height: "12px", display: "block" }}></span>
                      </p>
                    </div>
                  </td>
                  <td style={{ padding: "12px 8px", borderLeft: "none", borderRight: "none", verticalAlign: "middle" }}>
                    <p className="card-text placeholder-glow mb-0">
                      <span className="placeholder" style={{ width: "50px", height: "16px", display: "block", margin: "0 auto" }}></span>
                    </p>
                  </td>
                  <td style={{ padding: "12px 8px", borderLeft: "none", borderRight: "none", verticalAlign: "middle" }}>
                    <p className="card-text placeholder-glow mb-0">
                      <span className="placeholder" style={{ width: "50px", height: "16px", display: "block", margin: "0 auto" }}></span>
                    </p>
                  </td>
                  <td style={{ padding: "12px 8px", borderLeft: "none", borderRight: "none", verticalAlign: "middle" }}>
                    <p className="card-text placeholder-glow mb-0">
                      <span className="placeholder" style={{ width: "60px", height: "16px", display: "block", margin: "0 auto" }}></span>
                    </p>
                  </td>
                  <td style={{ padding: "12px 8px", borderLeft: "none", borderRight: "none", verticalAlign: "middle" }}>
                    <p className="card-text placeholder-glow mb-0">
                      <span className="placeholder" style={{ width: "60px", height: "16px", display: "block", margin: "0 auto" }}></span>
                    </p>
                  </td>
                  <td style={{ padding: "12px 8px", borderLeft: "none", borderRight: "none", verticalAlign: "middle", width: "45%" }}>
                    <div className="d-flex flex-column align-items-center gap-2">
                      <p className="card-text placeholder-glow mb-0">
                        <span className="placeholder" style={{ width: "140px", height: "14px", display: "block", margin: "0 auto" }}></span>
                      </p>
                      <p className="card-text placeholder-glow mb-0">
                        <span className="placeholder" style={{ width: "100%", height: "45px", display: "block", borderRadius: "6px" }}></span>
                      </p>
                      <div className="d-flex gap-2">
                        <span className="placeholder" style={{ width: "60px", height: "22px", display: "block", borderRadius: "12px" }}></span>
                        <span className="placeholder" style={{ width: "60px", height: "22px", display: "block", borderRadius: "12px" }}></span>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: "12px 8px", borderLeft: "none", borderRight: "none", verticalAlign: "middle" }}>
                    <div className="d-flex flex-column align-items-center gap-2">
                      <p className="card-text placeholder-glow mb-0">
                        <span className="placeholder" style={{ width: "80px", height: "14px", display: "block", margin: "0 auto" }}></span>
                      </p>
                      <div className="d-flex gap-2">
                        <span className="placeholder" style={{ width: "38px", height: "38px", display: "block", borderRadius: "50%" }}></span>
                        <span className="placeholder" style={{ width: "38px", height: "38px", display: "block", borderRadius: "50%" }}></span>
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <>
                {records.map(
                  (record, index) =>
                    record?.status && (
                      <RowIncident
                        record={record}
                        key={`${record.schedule.id}-${index}`}
                        removeRecord={() => removeRecord(record.schedule.id)}
                        index={index}
                        onAfterSave={onAfterSave}
                        updateIncidentRecord={updateIncidentRecord}
                        isAdmin={isAdmin}
                        branchMetaMap={branchMetaMap}
                        maxNightCutoffMinutes={maxNightCutoffMinutes}
                        showBranchName={showBranchName}
                        branchNameById={branchNameById}
                      />
                    )
                )}
                {unschedule.map((record, index) => {
                  const recordsCount = records.filter(r => r?.status).length;
                  return (
                    <UnscheduleRow
                      unschedule={record}
                      key={`unschedule-${record.partner.id}-${record.checkIn.id}-${index}`}
                      index={recordsCount + index}
                      removeUnscheduleRecord={() =>
                        removeUnscheduleRecord(
                          record.partner.id,
                          record.checkIn.id
                        )
                      }
                      onAfterSave={onAfterSave}
                      branchMetaMap={branchMetaMap}
                      maxNightCutoffMinutes={maxNightCutoffMinutes}
                      operationDate={operationDate}
                      showBranchName={showBranchName}
                      branchNameById={branchNameById}
                    />
                  );
                })}
              </>
            )}
          </tbody>
        </table>

        {Boolean(records.length === 0 && unschedule.length === 0) && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
              padding: "40px 20px",
              backgroundColor: "white",
            }}
          >
            <EmptyImage className="mx-auto w-250px" text="Sin incidencias" />
          </div>
        )}
      </div>
    </div>
  );
};

export default Incidents;
